import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  AlertCircle,
  Loader
} from 'lucide-react'

export const Route = createFileRoute('/admin/login')({
  component: Login,
})

function Login() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Clear errors when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      // Call API endpoint for login
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
        credentials: 'include', // Important: include cookies in request/response
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Store auth token in localStorage if provided, otherwise rely on cookies
        if (data.token) {
          localStorage.setItem('auth_token', data.token)
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user))
        }
        
        navigate({ to: '/admin' })
        return
      } else {
        setErrors({ password: data.error || 'Invalid email or password' })
      }
    } catch (error) {
      console.error('Login error:', error)
      setErrors({ password: 'An error occurred. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl text-brand-slate-800 tracking-tight">
            Admin Login
          </h2>
          <p className="text-brand-slate-500 mt-2">
            Login to manage your Pustaka Pranala admin panel
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white border border-brand-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="p-6 border-b border-brand-slate-200">
            <h3 className="text-lg text-brand-slate-800 text-center">
              Welcome Back
            </h3>
          </div>
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-brand-slate-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2 text-brand-yellow-500" strokeWidth={1.5} />
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'border-red-500' : ''}
                  placeholder="admin@pranala.id"
                  disabled={isLoading}
                />
                {errors.email && (
                  <div className="flex items-center mt-2 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 mr-1" strokeWidth={1.5} />
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-brand-slate-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-2 text-brand-yellow-500" strokeWidth={1.5} />
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`pr-10 ${errors.password ? 'border-red-500' : ''}`}
                    placeholder="Enter your password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-brand-slate-400 hover:text-brand-yellow-500" strokeWidth={1.5} />
                    ) : (
                      <Eye className="w-5 h-5 text-brand-slate-400 hover:text-brand-yellow-500" strokeWidth={1.5} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <div className="flex items-center mt-2 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 mr-1" strokeWidth={1.5} />
                    {errors.password}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-brand-yellow-400 hover:bg-brand-yellow-500 text-brand-slate-900 font-medium rounded-lg border-0 shadow-sm hover:shadow-md transition-all"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Demo Credentials */}
        <div className="text-center">
          <span className="px-2 py-0.5 bg-brand-yellow-100 text-brand-yellow-700 text-xs rounded-md font-medium mb-4 inline-block border border-brand-yellow-200">
            Demo Credentials
          </span>
          <div className="bg-brand-slate-100 border border-brand-slate-200 rounded-xl p-4 text-sm">
            <div className="text-brand-slate-500 mb-2">For demonstration purposes:</div>
            <div className="space-y-1 text-brand-slate-700">
              <div><strong>Email:</strong> admin@pranala.id</div>
              <div><strong>Password:</strong> Admin123!@#</div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="text-center text-sm text-brand-slate-500">
          <a
            href="/"
            className="text-brand-yellow-600 hover:text-brand-yellow-700 hover:underline"
          >
            ← Back to Website
          </a>
        </div>
      </div>
    </div>
  )
}
